import { useMemo } from 'react';

import styled from '@emotion/styled';
import { useHoverPopover } from '@kc-console/common-ui';
import { useScopedLinkBuilder } from '@kc-console/context';

import { useMegaMenuState } from '../../../../hooks';
import AllServiceMenuItem from './AllServiceMenuItem';
import CategoryMenuItem from './CategoryMenuItem';
import MenuItemHoverPopover from './MenuItemHoverPopover';

import type { PopoverOrigin } from '@kc-console/common-ui';
import type { MegaMenuService } from '@kc-console/context';

interface Props {
  service: MegaMenuService & { isFavorite?: boolean };
  type: 'all' | 'category';
}

const MenuItem = ({ service, type }: Props) => {
  const { menus } = service;
  const { isOpen, setIsOpen } = useMegaMenuState();
  const { getProps } = useScopedLinkBuilder();
  const hasHoverMenu = useMemo(() => {
    return isOpen && menus && menus.length > 0;
  }, [isOpen, menus]);
  const hoverPopoverProps = useHoverPopover<HTMLLIElement>({
    openTimeout: 300,
    closeTimeout: 50,
  });
  const { onMouseEnter, onMouseLeave, open, setAnchorEl } = hoverPopoverProps;
  const transformOrigin: PopoverOrigin = { vertical: 'top', horizontal: 'right' };

  const closeMegaMenu = () => {
    setIsOpen(false);
    setAnchorEl(null);
  };

  const MenuItem = type === 'all' ? AllServiceMenuItem : CategoryMenuItem;

  return (
    <li onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <LinkWrapper hover={open}>
        <StyledMenuItemAnchor {...getProps(service.path, { onClick: closeMegaMenu })}>
          <MenuItem service={service} open={open} />
        </StyledMenuItemAnchor>
        {hasHoverMenu && (
          <MenuItemHoverPopover
            {...hoverPopoverProps}
            menus={menus}
            transformOrigin={transformOrigin}
            handleMenuClick={closeMegaMenu}
          />
        )}
      </LinkWrapper>
    </li>
  );
};

const LinkWrapper = styled.div<{ hover: boolean }>`
  background-color: ${({ hover, theme }) => (hover ? theme.color['surface/hover'] : 'inherit')};

  &:active {
    background-color: ${({ theme }) => theme.color['surface/press']};
  }

  position: relative;
  ${({ theme }) =>
    theme.mixins.focusRing('inside', {
      selectors: {
        focus: '& > a:focus-visible',
      },
    })};
`;

const StyledMenuItemAnchor = styled('a')`
  text-decoration: none;
  color: #222;
`;

export default MenuItem;
